import * as React from "react"

import { cn } from "@/lib/utils"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { wrapperClassName?: string }
>(({ className, wrapperClassName, ...props }, ref) => (
  <div className={cn("relative w-full overflow-x-auto overflow-y-hidden", wrapperClassName)}>
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "sticky top-0 z-10 bg-white dark:bg-neutral-950 [&_tr]:border-b [&_tr]:border-neutral-200 dark:[&_tr]:border-neutral-700",
      className
    )}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t border-border bg-muted/30 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-neutral-200 dark:border-neutral-700 hover:bg-blue-50/40 dark:hover:bg-neutral-800/50 data-[state=selected]:bg-blue-50 dark:data-[state=selected]:bg-neutral-800",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-8 px-3 text-left align-middle text-xs font-normal text-muted-foreground uppercase tracking-wider whitespace-nowrap border-r border-neutral-200 dark:border-neutral-700 last:border-r-0 [&:has([role=checkbox])]:px-0 [&:has([role=checkbox])]:border-r-0",
      "group-data-[density=compact]/density:h-6 group-data-[density=compact]/density:px-1.5 group-data-[density=compact]/density:text-[10px]",
      "group-data-[density=default]/density:h-8",
      "group-data-[density=comfortable]/density:h-9",
      "group-data-[density=spacious]/density:h-10",
      "group-data-[textsize=small]/density:text-[10px]",
      "group-data-[textsize=medium]/density:text-xs",
      "group-data-[textsize=large]/density:text-[13px]",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "py-1.5 px-3 align-middle text-sm overflow-hidden text-ellipsis whitespace-nowrap border-r border-neutral-200 dark:border-neutral-700 last:border-r-0 [&:has([role=checkbox])]:px-0 [&:has([role=checkbox])]:border-r-0",
      "group-data-[density=compact]/density:py-0 group-data-[density=compact]/density:px-1.5 group-data-[density=compact]/density:h-9",
      "group-data-[density=default]/density:py-1.5 group-data-[density=default]/density:h-12",
      "group-data-[density=comfortable]/density:py-2 group-data-[density=comfortable]/density:h-14",
      "group-data-[density=spacious]/density:py-3 group-data-[density=spacious]/density:h-16",
      "group-data-[textsize=small]/density:text-xs",
      "group-data-[textsize=medium]/density:text-sm",
      "group-data-[textsize=large]/density:text-[15px]",
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
